<script setup lang="ts">
  import { onBeforeMount, ref, watch } from 'vue';
  import {
    DEFAULT_METADATA_VALUES_SEPARATOR,
    KksState,
    SlVueTreeSearchOptions,
    ITreeSelectKKS,
  } from 'types';
  import TreeSelect from './TreeSelect.vue';
  import {
    KksSelectorChangeEvent,
    LoadFunction,
    NodeModel,
  } from './KksSelector.types';
  import Tag from 'primevue/tag';
  import { KKSStatesColorTags } from './kksStatesColorTags.ts';
  import { KksTreeService } from '@/services';
  import { useToast } from '@/composables/useToast.ts';
  import { useI18n } from 'vue-i18n';
  import { computed } from 'vue';

  /* COMPOSABLES */
  const { createErrorToast } = useToast();

  const { t } = useI18n();

  /* PROPS */
  const props = defineProps({
    busy: { type: Boolean, required: false, default: false },
    canRemoveLast: { type: Boolean, required: false, default: true },
    disabled: { type: Boolean, required: false, default: false },
    label: { type: String, required: true },
    location: { type: String, required: true },
    showFilterButton: { type: Boolean, required: false, default: true },
    required: { type: Boolean, required: false },
    workspaceId: { type: String, required: false },
  });

  const { label } = props;
  const model = defineModel<string>();

  const emit = defineEmits<{
    (e: 'change', payload: KksSelectorChangeEvent, kks: ITreeSelectKKS[]): void;
  }>();

  /* DATA */
  const nodes = ref<NodeModel<ITreeSelectKKS>[]>([]);
  const kksArray = ref<ITreeSelectKKS[]>([]);
  const kksStates = ref<KksState[]>([]);
  const isLoadingPreselected = ref<boolean>(false);
  const isLoadingTreeNodes = ref<boolean>(false);
  const isBusy = computed(() => {
    return props.busy || isLoadingPreselected.value || isLoadingTreeNodes.value;
  });

  const getSelectedKks = (): ITreeSelectKKS[] => {
    return kksArray.value;
  };

  /* METHODS */
  const loadKKSTreeNodes = async (
    branchNode?: string | null,
    searchTerm?: string,
    offset?: number,
    limit?: number,
    searchMode?: SlVueTreeSearchOptions,
    state?: KksState[] | null,
  ): Promise<{ data: NodeModel<ITreeSelectKKS>[]; count: number }> => {
    const nodes = await KksTreeService.getKksTree({
      locationScope: props.location,
      branchNode,
      searchTerm,
      offset,
      limit,
      searchMode,
      state: state ?? undefined,
      workspaceId: props.workspaceId,
    });

    const deniedNodes = nodes.data.filter(
      (n: ITreeSelectKKS) => n.state === KksState.NON_CONFORMING,
    );
    const cleanNodes = nodes.data.filter(
      (n: ITreeSelectKKS) => n.state !== KksState.NON_CONFORMING,
    );

    const treeData: any[] = cleanNodes.map((n: ITreeSelectKKS) => ({
      title: n.kks_trimmed,
      isExpanded: false,
      isDraggable: false,
      isSelectable: false,
      data: { ...n },
    }));

    if (deniedNodes.length > 1) {
      const deniedChilds = deniedNodes.map((dn: ITreeSelectKKS) => {
        return {
          _id: dn._id,
          title: dn.kks_trimmed,
          isExpanded: false,
          isDraggable: false,
          isSelectable: false,
          data: dn,
        };
      });
      treeData.push({
        title: KksState.NON_CONFORMING,
        isExpanded: false,
        isDraggable: false,
        isSelectable: false,
        data: {
          _id: '',
          childStates: {
            [KksState.NON_CONFORMING]: deniedNodes.length,
          },
          kks_trimmed: KksState.NON_CONFORMING,
          state: KksState.NON_CONFORMING,
        },
        children: deniedChilds,
      });
    }

    return {
      data: treeData,
      count: nodes.count,
    };
  };

  /** WATCHERS */
  watch(
    kksArray,
    async (newVal) => {
      const newKks = newVal.map((n) => n.kks_trimmed);
      const deduplicatedNewKks = Array.from(new Set(newKks));
      const joinedNewKks = deduplicatedNewKks.join(
        DEFAULT_METADATA_VALUES_SEPARATOR,
      );
      if (joinedNewKks === model.value) return;
      model.value = joinedNewKks;
      emit('change', { target: { type: 'input', value: newVal } }, newVal);
    },
    { deep: true },
  );

  /* LIFECYCLE */
  onBeforeMount(async () => {
    if (model.value?.length) {
      try {
        const preselectedKks = model.value.split(
          DEFAULT_METADATA_VALUES_SEPARATOR,
        );
        isLoadingPreselected.value = true;
        /**
         * Initially populating the component with preselelected KKS values
         * before checking their status.
         */
        kksArray.value = preselectedKks.map((kks) =>
          KksTreeService.toKksNode(kks),
        );

        const kksNodes = await KksTreeService.getPreselectedKksNodes(
          preselectedKks,
          props.location,
        );

        /**
         * Replacing `kksArray` with fetched KKS nodes.
         */
        kksArray.value = kksNodes;
      } catch {
        createErrorToast(t('kksPicker.errors.errorGettingKks'));
      } finally {
        isLoadingPreselected.value = false;
      }
    }

    Object.values(KksState).forEach((s) => {
      kksStates.value?.push(s);
    });
    try {
      isLoadingTreeNodes.value = true;
      nodes.value = (
        await loadKKSTreeNodes('null', '', 0, 200, undefined, kksStates.value)
      ).data;
    } catch {
      createErrorToast(t('kksPicker.errors.errorGettingKks'));
    } finally {
      isLoadingTreeNodes.value = false;
    }
  });

  defineExpose({
    getSelectedKks,
  });
</script>

<template>
  <TreeSelect
    v-model="kksArray"
    :busy="isBusy"
    :can-remove-last="canRemoveLast"
    :disabled="disabled"
    :value="nodes"
    :label="label"
    :load="loadKKSTreeNodes as LoadFunction"
    :location="location"
    :filter-option="kksStates"
    :show-filter-button="showFilterButton"
    :workspace-id="workspaceId"
    tag-key="kks_trimmed"
  >
    <template #option="{ node }">
      <div class="flex items-center gap-3">
        {{ node.title }}
        <Tag
          :value="node.data.state"
          :style="KKSStatesColorTags[node.data.state]"
          name="tree-select-title-tag"
          style="height: 1rem; font-weight: 500"
        ></Tag>
      </div>
    </template>
  </TreeSelect>
</template>
